// ---------------------------------------------------------------------------
// useFormFields — owns the form's editable scalar fields plus the variables
// list (and its mutators). Mirrors useTaskDrafts: state + setters live here
// so the form's body can focus on render + composition.
//
// Per-field useStates are kept (rather than collapsed into one
// useReducer/useState<FormFields>) so Fluent UI inputs can wire directly
// to setters — `onChange={setJobName}` — without an update-fn wrapper.
// ---------------------------------------------------------------------------

import { useCallback, useState } from 'react';

import type {
  AgentTypeChoice,
  DataLakeFileSelector,
  DataSourceMode,
  ParallelismChoice,
  SeededFormState,
  VariableRow,
} from '../EvaluationTemplateForm.types.js';
import { uid } from '../EvaluationTemplateForm.utils.js';

export interface UseFormFieldsResult {
  // Job identity
  jobName: string;
  setJobName: (s: string) => void;
  jobDisplayName: string;
  setJobDisplayName: (s: string) => void;
  jobDescription: string;
  setJobDescription: (s: string) => void;

  // Query set
  querySetMode: DataSourceMode;
  setQuerySetMode: (m: DataSourceMode) => void;
  queryInlineText: string;
  setQueryInlineText: (s: string) => void;

  // Data set
  dataSetEnabled: boolean;
  setDataSetEnabled: (b: boolean) => void;
  dataSetMode: DataSourceMode;
  setDataSetMode: (m: DataSourceMode) => void;
  dataInlineText: string;
  setDataInlineText: (s: string) => void;
  dataSetExistingId: string | null;
  setDataSetExistingId: (id: string | null) => void;
  dataSetExistingVersion: number | null;
  setDataSetExistingVersion: (v: number | null) => void;
  dataSetFileSelector: DataLakeFileSelector[];
  setDataSetFileSelector: (s: DataLakeFileSelector[]) => void;

  // Agent type — only 'llm' is implemented; the segmented control is
  // included anyway so the UI mirrors the design and we have a place to
  // wire HTTP-proxy / custom later.
  agentType: AgentTypeChoice;
  setAgentType: (c: AgentTypeChoice) => void;

  // Concurrency
  workersText: string;
  setWorkersText: (s: string) => void;
  parallelism: ParallelismChoice;
  setParallelism: (p: ParallelismChoice) => void;

  // Variables (list + per-row mutators)
  variables: VariableRow[];
  addVariable: () => void;
  updateVariable: (id: string, patch: Partial<VariableRow>) => void;
  removeVariable: (id: string) => void;
}

export function useFormFields(
  seeded: SeededFormState | null,
): UseFormFieldsResult {
  // Job identity
  const [jobName, setJobName] = useState(() => seeded?.jobName ?? '');
  const [jobDisplayName, setJobDisplayName] = useState(
    () => seeded?.jobDisplayName ?? '',
  );
  const [jobDescription, setJobDescription] = useState(
    () => seeded?.jobDescription ?? '',
  );

  // Query set
  const [querySetMode, setQuerySetMode] = useState<DataSourceMode>(
    () => seeded?.querySetMode ?? 'inline',
  );
  const [queryInlineText, setQueryInlineText] = useState(
    () => seeded?.queryInlineText ?? '',
  );

  // Data set
  const [dataSetEnabled, setDataSetEnabled] = useState(
    () => seeded?.dataSetEnabled ?? false,
  );
  const [dataSetMode, setDataSetMode] = useState<DataSourceMode>(
    () => seeded?.dataSetMode ?? 'inline',
  );
  const [dataInlineText, setDataInlineText] = useState(
    () => seeded?.dataInlineText ?? '',
  );
  const [dataSetExistingId, setDataSetExistingId] = useState<string | null>(
    () => seeded?.dataSetExistingId ?? null,
  );
  const [dataSetExistingVersion, setDataSetExistingVersion] = useState<
    number | null
  >(() => seeded?.dataSetExistingVersion ?? null);
  const [dataSetFileSelector, setDataSetFileSelector] = useState<
    DataLakeFileSelector[]
  >(() => seeded?.dataSetFileSelector ?? []);

  // Agent type
  const [agentType, setAgentType] = useState<AgentTypeChoice>('llm');

  // Concurrency
  const [workersText, setWorkersText] = useState(
    () => seeded?.workersText ?? '',
  );
  const [parallelism, setParallelism] = useState<ParallelismChoice>(
    () => seeded?.parallelism ?? 'task',
  );

  // Variables
  const [variables, setVariables] = useState<VariableRow[]>(
    () => seeded?.variables ?? [],
  );

  const addVariable = useCallback(() => {
    setVariables((rows) => [
      ...rows,
      {
        id: uid('v'),
        name: '',
        type: 'string',
        defaultValue: '',
        description: '',
      },
    ]);
  }, []);

  const updateVariable = useCallback(
    (id: string, patch: Partial<VariableRow>) => {
      setVariables((rows) =>
        rows.map((r) => (r.id === id ? { ...r, ...patch } : r)),
      );
    },
    [],
  );

  const removeVariable = useCallback((id: string) => {
    setVariables((rows) => rows.filter((r) => r.id !== id));
  }, []);

  return {
    jobName,
    setJobName,
    jobDisplayName,
    setJobDisplayName,
    jobDescription,
    setJobDescription,
    querySetMode,
    setQuerySetMode,
    queryInlineText,
    setQueryInlineText,
    dataSetEnabled,
    setDataSetEnabled,
    dataSetMode,
    setDataSetMode,
    dataInlineText,
    setDataInlineText,
    dataSetExistingId,
    setDataSetExistingId,
    dataSetExistingVersion,
    setDataSetExistingVersion,
    dataSetFileSelector,
    setDataSetFileSelector,
    agentType,
    setAgentType,
    workersText,
    setWorkersText,
    parallelism,
    setParallelism,
    variables,
    addVariable,
    updateVariable,
    removeVariable,
  };
}
